import { useState, useEffect, Fragment, useRef } from 'react';
import './header.scss';
import ParticlesBackground from './particle-background/particle-background';
import { useTheme } from '../../contexts/themeContext';
import { ReactTyped } from 'react-typed';

import HeapSortVisualizer from './heap-sort-visualizer/heap-sort-visualizer';
import VoronoiHeader from './voronoi-header/voronoi-header';
// removed unused image imports
import BusinessImageSvg from './header-image/business';
import CasualImageSvg from './header-image/casual';
import CasualVoronoiImageSvg from './header-image/casual-voronoi';
import TalkToMeBubble from '../cv-chat/TalkToMeBubble';

import { useLanguage } from '../../contexts/languageContext';
interface HeaderProps {
  onHeadheightChange: (headminified: boolean) => void;
}

/** Le due possibili facce del tema casual. */
type CasualVariant = 'heapsort' | 'voronoi';

const pickCasualVariant = (): CasualVariant =>
  Math.random() < 0.5 ? 'heapsort' : 'voronoi';

const Header: React.FC<HeaderProps> = ({ onHeadheightChange }) => {
  const [isHeadminified, setHeadminified] = useState(false); // Stato per gestire la classe
  const [isScrollPrevented, setIsScrollPrevented] = useState(false); // Stato per prevenire lo scroll per 2 secondi
  const { t } = useLanguage();
  const isHeadminifiedRef = useRef(isHeadminified);
  /* <img
  src={theme !== "business" ? casualimage : businessimage}
  alt="profile"
  className="img-fluid rounded-circle profile-image"


/>*/
  const { theme } = useTheme();

  /* Quale delle due facce del tema casual mostrare. Si estrae quando il tema
     casual entra in scena - al primo mount se e' quello iniziale, e a ogni
     ritorno da business - e resta poi stabile: se dipendesse dal render la
     variante cambierebbe a ogni scroll che minifica l'header. */
  const [casualVariant, setCasualVariant] = useState<CasualVariant>(pickCasualVariant);
  const prevThemeRef = useRef(theme);
  useEffect(() => {
    if (theme === 'casual' && prevThemeRef.current !== 'casual') {
      setCasualVariant(pickCasualVariant());
    }
    prevThemeRef.current = theme;
  }, [theme]);

  const isCasual = theme !== 'business';
  const showVoronoi = isCasual && casualVariant === 'voronoi';

  /* Il Voronoi misura questo elemento: il rettangolo che occupa e' il
     contenitore in cui adatta la cella della foto, e il suo centro e' dove
     mette il seme ospite. */
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrolled = currentScrollY > 70;


      // Se lo scroll è prevenuto (nei 2 secondi dopo la minificazione), mantieni fisso a 71px
      if (isScrollPrevented) {
        console.debug('Scroll prevented - keeping position at 71px');
        window.scrollTo(0, 71);
        return;
      }

  // debug logs removed in production for performance

      // Se l'header non è ancora minificato e si sta scrollando oltre i 71px
  if (scrolled && !isHeadminifiedRef.current) {
        console.debug('Header minifying - stopping scroll at 71px');

        window.scrollTo(0, 71);

        // Aggiorna lo stato dell'header
  isHeadminifiedRef.current = true;
        setHeadminified(true);
        onHeadheightChange(true);

        // Previeni lo scroll per 2 secondi
        setIsScrollPrevented(true);
        setTimeout(() => {
          setIsScrollPrevented(false);
          console.debug('Scroll prevention ended');
        }, 500);

        return; // Esce dalla funzione per evitare ulteriori elaborazioni
      }

      // Se l'header è minificato e si scrolla verso l'alto sotto i 50px
  if (!scrolled && isHeadminifiedRef.current) {
        console.debug('Header expanding');
    isHeadminifiedRef.current = false;
        setHeadminified(false);
        onHeadheightChange(false);
      }
    };

  window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [onHeadheightChange, isScrollPrevented]);
  return (
    <Fragment>
      <header
        className={`header  d-flex align-items-center justify-content-center   ${isHeadminified ? 'header-small' : 'header-large'} ${isCasual  ? `header-casual-${casualVariant}` : ''}  no-print`}
      >
        {!isCasual ? (
          <ParticlesBackground isHeaderMinified={isHeadminified} />
        ) : showVoronoi ? (
          /* respectReducedMotion={false}: la deriva va anche con
             prefers-reduced-motion attivo. E' una scelta deliberata, non una
             dimenticanza, e la ragione e' che qui la preferenza intercetta la cosa
             sbagliata. Su Windows quel segnale arriva dall'unico interruttore
             "Effetti animazione", che e' spento su molte macchine per prestazioni
             o per gusto, e non solo da chi ha problemi vestibolari; e il movimento
             in questione e' un'orbita di ~17px con periodo di ~13 secondi, cioe'
             circa 1.3px al secondo - non il movimento ampio e rapido che la
             preferenza esiste per sopprimere. Il costo di rispettarla era uno
             sfondo statico per una fetta larga di visitatori.
             Se la valutazione cambia, togliere la prop: il default del componente
             rispetta la preferenza.

             Nessun focusPadding: la cella della foto si adatta esattamente al
             rettangolo che l'elemento in pagina riserva nella colonna. Il
             componente disegna la foto dentro la propria tassellazione - vedi
             casual-voronoi.tsx per il perche' quell'elemento sia vuoto - quindi
             non c'e' piu' una macchia da gonfiare per contenere una forma
             disegnata in un altro sistema di coordinate. */
          <VoronoiHeader focusRef={profileRef} respectReducedMotion={false} />
        ) : (
          <HeapSortVisualizer />
        )}

  <div className="container-fluid" >
          <div className="row row-header align-items-center">
            <div className={`image-container col-12 col-md-4 col-lg-3 p-2  text-center`}>
              {/* L'anchor fornisce il riferimento di posizione al fumetto che
                  porta a /talktome, e al Voronoi il rettangolo in cui adattare
                  la cella della foto. */}
              <div className="cv-chat-bubble-anchor" ref={profileRef}>
                {/* Nella variante Voronoi qui non si disegna la foto: la disegna
                    VoronoiHeader dentro la propria tassellazione, e questo
                    elemento resta vuoto per riservarne lo spazio. Il perche' sta
                    in casual-voronoi.tsx. */}
                {theme !== 'business' ? (
                  showVoronoi ? (
                    <CasualVoronoiImageSvg></CasualVoronoiImageSvg>
                  ) : (
                    <CasualImageSvg></CasualImageSvg>
                  )
                ) : (
                  <BusinessImageSvg></BusinessImageSvg>
                )}
                <TalkToMeBubble />
              </div>
            </div>
            <div className={` d-none d-md-block col-12 col-md-8 col-lg-9 p-2   `}>
              <div className="intro-content display-table text-light mt-4">
                {theme !== 'business' ? (
                  <div className="container code">
                    <h1 className="intro-title mb-4 fw-bold">{t('hello')}</h1>
                    <p className="intro-subtitle">
                      <span className="text-slider-items fw-bold"></span>
                      <strong className="text-slider">
                        <ReactTyped
                          strings={[
                            'Senior Full Stack Developer',
                            'Software Architect',
                            'Computer Scientist Engineer',
                            'DevOps Engineer',
                            t('UnwaveringOptimist'),
                            'Problem Solver',
                            'Focus on Cybersecurity',
                          ]}
                          typeSpeed={80}
                          backDelay={1100}
                          backSpeed={30}
                          loop
                        />
                      </strong>
                    </p>
                    {/* <p className="pt-3">
                <a
                  className="btn btn-primary btn js-scroll px-4"
                  href="#work"
                  role="button"
                >
                  View My Work
                </a>
              </p>*/}
                  </div>
                ) : (
                  <div className="container ">
                    <h1 className="intro-title mb-4">{t('hello')}</h1>
                    <p className="intro-subtitle">
                      <span className="text-slider-items"></span>
                      <strong className="text-slider">
                        <ReactTyped
                          strings={[t('businessPresentation')]}
                          typeSpeed={20}
                          backDelay={1100}
                          backSpeed={30}
                        />
                      </strong>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div
        className={`header d-none d-flex header-printable align-items-center justify-content-center  header-small   `}
      >
  <div className="container-fluid" >
          <div className="row row-header align-items-center">
            <div className={`image-container col-3 p-2  text-center`}>
              {theme !== 'business' ? (
                <CasualImageSvg></CasualImageSvg>
              ) : (
                <BusinessImageSvg></BusinessImageSvg>
              )}
            </div>
            <div className={`   d-md-block col-9 p-2   `}>
              <div className="  display-table text-light mt-4">
                <div className="container ">
                  <h1 className="  mb-4">{t('hello')}</h1>
                  <p>
                    <strong>{t('businessPresentation')}</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};
export default Header;
